const EVENT_NAME = 'theme:cupid-hearts';

const DEBUG = (() => {
  try {
    return new URLSearchParams(window.location.search).get('cupid_debug') === '1';
  } catch (e) {
    return false;
  }
})();

/** @param {...any} args */
function debug(...args) {
  if (!DEBUG) return;
  // eslint-disable-next-line no-console
  console.log('[cupid-hearts]', ...args);
}

/** @returns {any} */
function getTheme() {
  // Theme is declared in a global <script> in snippets/scripts.liquid.
  // In this theme, ES modules reference it as a global binding.
  // eslint-disable-next-line no-undef
  return typeof Theme !== 'undefined' ? Theme : undefined;
}

function isEnabled() {
  try {
    const theme = getTheme();
    if (theme?.features) {
      return Boolean(theme.features.cupidHearts) && String(theme.features.cartType) === 'drawer';
    }

    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer instanceof HTMLElement) {
      return String(cartDrawer.dataset.cupidHeartsEnabled) === 'true';
    }

    return false;
  } catch (e) {
    return false;
  }
}

function prefersReducedMotion() {
  return Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
}

function canUsePopover() {
  try {
    return typeof HTMLElement.prototype.showPopover === 'function';
  } catch (e) {
    return false;
  }
}

/**
 * @returns {{ overlay: HTMLElement; originLeft: number; originTop: number; mode: 'popover' | 'dialog' }}
 */
function getOrCreateOverlayWithOrigin() {
  // Prefer Popover API so we can draw above <dialog> (top-layer)
  // and survive Section Rendering API swaps.
  if (canUsePopover()) {
    /** @type {HTMLElement | null} */
    let overlay = document.body.querySelector('.cupid-hearts-overlay[popover="manual"]');

    // If an old overlay exists inside the cart drawer/dialog (from fallback mode), don't reuse it.
    // It can be absolutely positioned and will cause coordinates to be wrong.
    if (!(overlay instanceof HTMLElement)) {
      const stray = document.querySelector('.cupid-hearts-overlay');
      if (stray instanceof HTMLElement && stray.parentElement !== document.body) {
        stray.remove();
      }

      overlay = document.createElement('div');
      overlay.className = 'cupid-hearts-overlay';
      overlay.setAttribute('aria-hidden', 'true');
      overlay.setAttribute('popover', 'manual');
      // Ensure full-screen overlay in top-layer.
      overlay.style.position = 'fixed';
      overlay.style.inset = '0';
      overlay.style.margin = '0';
      overlay.style.padding = '0';
      overlay.style.border = '0';
      overlay.style.background = 'transparent';
      overlay.style.boxShadow = 'none';
      overlay.style.width = '100vw';
      overlay.style.height = '100vh';
      overlay.style.maxWidth = '100vw';
      overlay.style.maxHeight = '100vh';
      document.body.appendChild(overlay);
    }

    // Re-apply critical styles in case this overlay existed from a previous page load.
    overlay.style.position = 'fixed';
    overlay.style.inset = '0';
    overlay.style.margin = '0';
    overlay.style.padding = '0';
    overlay.style.border = '0';
    overlay.style.background = 'transparent';
    overlay.style.boxShadow = 'none';
    overlay.style.width = '100vw';
    overlay.style.height = '100vh';
    overlay.style.maxWidth = '100vw';
    overlay.style.maxHeight = '100vh';

    try {
      // showPopover is idempotent if already open
      overlay.showPopover();
    } catch (e) {
      // ignore
    }

    // Fixed full-screen overlay: local coords == viewport coords
    return { overlay, originLeft: 0, originTop: 0, mode: 'popover' };
  }

  const dialog = document.querySelector('cart-drawer-component dialog[open]') || document.querySelector('cart-drawer-component dialog');
  const container = dialog instanceof HTMLElement ? dialog : document.body;

  /** @type {HTMLElement | null} */
  let overlay = container.querySelector('.cupid-hearts-overlay');
  if (!(overlay instanceof HTMLElement)) {
    overlay = document.createElement('div');
    overlay.className = 'cupid-hearts-overlay';
    overlay.setAttribute('aria-hidden', 'true');
    // Inside dialog we need absolute so it fills the dialog box.
    overlay.style.position = container.tagName === 'DIALOG' ? 'absolute' : 'fixed';
    if (container.tagName === 'DIALOG') {
      overlay.style.inset = '0';
    }
    container.appendChild(overlay);
  }

  const rect = container instanceof HTMLElement ? container.getBoundingClientRect() : { left: 0, top: 0 };
  return { overlay, originLeft: rect.left, originTop: rect.top, mode: 'dialog' };
}

function getOrCreateOverlay() {
  return getOrCreateOverlayWithOrigin().overlay;
}

function cooldownOk() {
  try {
    const storageKey = 'theme:cupid-hearts-last-played';
    const last = Number(sessionStorage.getItem(storageKey) || '0');
    if (Number.isFinite(last) && Date.now() - last < 8000) return false;
    sessionStorage.setItem(storageKey, String(Date.now()));
    return true;
  } catch (e) {
    return true;
  }
}

/**
 * @param {{
 *  x?: number;
 *  y?: number;
 *  rect?: { left: number; top: number; width: number; height: number };
 * }} position
 */
function spawnHearts(position) {
  const { overlay, originLeft, originTop, mode } = getOrCreateOverlayWithOrigin();

  let left = 0;
  let top = 0;
  let width = 0;
  let height = 0;

  if (position.rect && Number.isFinite(position.rect.left) && Number.isFinite(position.rect.top)) {
    left = position.rect.left;
    top = position.rect.top;
    width = position.rect.width;
    height = position.rect.height;
  } else {
    left = Number(position.x);
    top = Number(position.y);
    width = 0;
    height = 0;
  }

  // If drawing in dialog, convert viewport coords to dialog-local coords.
  const leftLocal = mode === 'dialog' ? left - originLeft : left;
  const topLocal = mode === 'dialog' ? top - originTop : top;
  const widthLocal = width;
  const heightLocal = height;

  const emojis = ['💗', '💖', '💕', '💘', '❤️'];
  const count = 16;

  for (let i = 0; i < count; i++) {
    const heart = document.createElement('span');
    heart.className = 'cupid-hearts-heart';
    const emoji = emojis[Math.floor(Math.random() * emojis.length)] || '💗';
    heart.textContent = emoji;

    // Spread across the full input width (if provided), otherwise around the single point.
    const spread = widthLocal > 0 ? (0.15 + Math.random() * 0.7) : 0;
    const baseX = widthLocal > 0 ? leftLocal + widthLocal * spread : leftLocal;
    const baseY = heightLocal > 0 ? topLocal + heightLocal * 0.42 : topLocal;

    const xJitter = (Math.random() - 0.5) * (widthLocal > 0 ? 18 : 56);
    const yJitter = (Math.random() - 0.5) * 10;
    const dx = (Math.random() - 0.5) * 72;
    const dy = -175 - Math.random() * 175;
    const delay = Math.random() * 0.40;
    const duration = 5.4 + Math.random() * 2.0;
    const size = 20 + Math.random() * 12;
    const opacity = 0.12 + Math.random() * 0.16;

    heart.style.setProperty('--x', `${baseX + xJitter}px`);
    heart.style.setProperty('--y', `${baseY + yJitter}px`);
    heart.style.setProperty('--dx', `${dx.toFixed(1)}px`);
    heart.style.setProperty('--dy', `${dy.toFixed(1)}px`);
    heart.style.setProperty('--delay', `${delay.toFixed(2)}s`);
    heart.style.setProperty('--dur', `${duration.toFixed(2)}s`);
    heart.style.setProperty('--size', `${size.toFixed(0)}px`);
    heart.style.setProperty('--opacity', `${opacity.toFixed(2)}`);

    heart.addEventListener('animationend', () => heart.remove(), { once: true });
    overlay.appendChild(heart);
  }

  window.setTimeout(() => {
    if (overlay instanceof HTMLElement && overlay.childElementCount === 0) overlay.remove();
  }, 11500);

  // If we're using popover, hide it after the animation window.
  if (mode === 'popover') {
    window.setTimeout(() => {
      try {
        overlay.hidePopover();
      } catch (e) {
        // ignore
      }
    }, 12000);
  }
}

document.addEventListener(EVENT_NAME, (event) => {
  if (!isEnabled()) {
    debug('disabled (Theme.features.cupidHearts=false or cartType!=drawer)');
    return;
  }
  if (prefersReducedMotion()) {
    debug('disabled (prefers-reduced-motion)');
    return;
  }
  if (!cooldownOk()) {
    debug('cooldown active');
    return;
  }

  const detail = event instanceof CustomEvent ? event.detail : null;
  const x = Number(detail?.x);
  const y = Number(detail?.y);
  const rect = detail?.rect && typeof detail.rect === 'object' ? detail.rect : null;

  // Defer until after any Section Rendering API morph refresh completes.
  window.setTimeout(() => {
    // Prefer passed rect so we can spread across the input width.
    if (
      rect &&
      Number.isFinite(Number(rect.left)) &&
      Number.isFinite(Number(rect.top)) &&
      Number.isFinite(Number(rect.width)) &&
      Number.isFinite(Number(rect.height))
    ) {
      debug('spawning from rect', rect);
      spawnHearts({ rect: { left: Number(rect.left), top: Number(rect.top), width: Number(rect.width), height: Number(rect.height) } });
      return;
    }

    // If for any reason coords are missing, fall back to the current discount input position.
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      const input = document.querySelector('cart-drawer-component dialog[open] input[name="discount"]');
      if (!(input instanceof HTMLInputElement)) return;
      const rect = input.getBoundingClientRect();
      debug('coords missing; using input rect');
      spawnHearts({ rect: { left: rect.left, top: rect.top, width: rect.width, height: rect.height } });
      return;
    }

    debug('spawning', { x, y });
    spawnHearts({ x, y });
  }, 0);
});
